/* ════════════════════════════════════════════════════════════════════
   FB REELS HEALTH — tab admin (Kelola Sistem)
   ════════════════════════════════════════════════════════════════════

   Menjawab SATU soalan (permintaan user 2026-09-20): berapa ramai
   pelanggan kena error automation Facebook Reels, dan berapa banyak.

   ⛔ TIGA PERKARA YANG MENENTUKAN REKA BENTUK SKRIN INI — jangan ubah
      tanpa membaca kepala `functions/fbHealth.js` dahulu:

   1. Error dikumpul ikut SIAPA YANG PERLU BERTINDAK (baldi A/B/C/D),
      bukan ikut nama langkah robot. Sebabnya: separuh daripada "Failed"
      sebenarnya AMARAN PALSU (reel memang menjadi). Senarai rata tanpa
      kumpulan menipu — ia buat keadaan nampak 2× lebih teruk.
   2. Tarikh yang dipapar ialah **ANGGARAN** — ia datang daripada masa
      kad ditulis (`lastUpdated`). Diukur pada data sebenar: betul dalam
      2 minit untuk 67% kad, tersasar berjam untuk ~15%. Sebab itu
      setiap tempat yang memapar masa WAJIB kekal berlabel "anggaran".
      Robot menulis JAM sahaja dalam log — tiada tarikh langsung.
   3. Skrin ini membaca SATU dokumen ringkasan (`admin_reports/fb_health`)
      yang ditulis oleh server. ⛔ JANGAN tukar kepada imbasan di sini —
      ia perlu membaca kad SEMUA 1,800+ akaun (~7,300 bacaan).

   ⚠️ KOD KATEGORI di bawah ialah KONTRAK dengan `functions/fbHealth.js`.
      Server mengelas kepada KOD; ayat pelanggan-admin duduk DI SINI
      sahaja. Kod yang server hantar tetapi tiada di sini dipapar sebagai
      kod mentah — bising, bukan senyap. Itu memang dikehendaki.
   ════════════════════════════════════════════════════════════════════ */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db, functions } from '../firebase';
import {
    AlertTriangle, CheckCircle2, ChevronRight, Facebook, Loader2, RefreshCw, Search,
} from 'lucide-react';

import '../design/tokens.css';
import '../design/ui.css';
import './FbHealthTab.css';

/* ─────────── Baldi: siapa yang kena bertindak ─────────── */
const BALDI = {
    A: { tajuk: 'False alarm — reel most likely went through', warna: '#EF9F27' },
    B: { tajuk: 'Our robot — needs fixing', warna: '#E24B4A' },
    C: { tajuk: 'Customer must fix', warna: '#378ADD' },
    D: { tajuk: 'Unknown — needs a look', warna: '#888780' },
    Z: { tajuk: 'Unknown — needs a look', warna: '#888780' },
};
const TURUTAN_BALDI = ['A', 'B', 'C', 'D', 'Z'];

/* ─────────── Kategori: ayat admin (English) + penjelasan (BM) ─────────── */
const KATEGORI = {
    A1: {
        tajuk: 'Scheduled, but robot could not see it in the list',
        apa: 'Robot dah tekan Schedule dan reel kemungkinan besar memang masuk. Senarai Scheduled di Facebook lambat dikemas kini, jadi robot tak nampak reel itu lalu tanda merah.',
        buat: 'Pesan pelanggan semak tab Scheduled dahulu sebelum Retry — kalau tidak reel jadi dua.',
    },
    A2: {
        tajuk: 'Draft saved, but robot could not see it',
        apa: 'Sama seperti amaran palsu jadual, tetapi untuk senarai Drafts. Draf kemungkinan besar dah tersimpan.',
        buat: 'Semak tab Drafts di Facebook sebelum Run semula.',
    },
    A3: {
        tajuk: 'Posted, but robot could not confirm it',
        apa: 'Butang Post ditekan tetapi composer masih ada selepas 30 saat, jadi robot tak dapat sahkan reel terbit.',
        buat: 'Semak page Facebook pelanggan sebelum Run semula.',
    },
    B1: {
        tajuk: 'Page reloaded mid-run, robot lost',
        apa: 'Halaman Facebook bertukar atau tab ditutup semasa robot sedang bekerja. Robot hilang dari halaman, dan reel selepas itu dalam larian yang sama turut gagal.',
        buat: 'Kena siasat. Satu kejadian boleh gagalkan banyak reel sekali gus.',
    },
    B2: {
        tajuk: 'Create reel window did not open',
        apa: 'Robot tekan Create lalu Reel di Content Library, tetapi tetingkap Create reel tak muncul.',
        buat: 'Kena siasat — mula dengan pelanggan yang paling banyak kena.',
    },
    B3: {
        tajuk: 'Add video button not found',
        apa: 'Tetingkap Create reel terbuka, tetapi butang Add video tak dijumpai.',
        buat: 'Kena siasat.',
    },
    B4: {
        tajuk: 'Video could not be attached',
        apa: 'Video gagal dimasukkan ke Facebook dari extension.',
        buat: 'Kena siasat.',
    },
    B5: {
        tajuk: 'Facebook slow to process the video',
        apa: 'Video dah naik, tetapi butang seterusnya tak hidup dalam tempoh robot menunggu.',
        buat: 'Mungkin perlu tunggu lebih lama. Ukur dahulu sebelum tukar nombor.',
    },
    B6: {
        tajuk: 'Schedule failed, draft fallback failed too',
        apa: 'Masa jadual tak diterima Facebook (selalunya terlalu hampir dengan masa sekarang), dan simpan draf sebagai ganti pun gagal.',
        buat: 'Reel tidak tersimpan di mana-mana — pelanggan kena Run semula.',
    },
    B7: {
        tajuk: 'Robot stopped at another step',
        apa: 'Robot berhenti pada langkah lain dalam laluan (caption, AI label, jadual, post).',
        buat: 'Baca sebab penuh pada baris error untuk tahu langkah mana.',
    },
    B8: {
        tajuk: 'Product CTA (Link name) did not stick',
        apa: 'Robot isi CTA ke kotak "Link name" dalam dialog Add product, baca semula, dan ia tetap tak melekat selepas 3 cubaan. Biasanya Facebook melukis semula dialog itu. Reel TIDAK dihantar — kita tak biar pautan produk keluar dengan teks salah.',
        buat: 'Suruh pelanggan Retry dahulu. Kalau berulang pada ramai pelanggan, Facebook mungkin dah tukar dialog Add product — robot kena dibaiki.',
    },
    B9: {
        tajuk: 'AI label switch could not be set',
        apa: 'Robot tak dapat hidupkan/matikan suis "Add AI label" selepas 3 cubaan (suis tak dijumpai, atau ditekan tetapi tak bertukar). Reel TIDAK dihantar.',
        buat: 'Suruh pelanggan Retry. Kalau berulang, minta dia tukar tetapan AI Label di Control Panel; kalau ramai kena, susun atur composer Facebook dah berubah.',
    },
    C1: {
        tajuk: 'Shopee link rejected by Facebook',
        apa: 'Facebook hanya terima link pendek affiliate Shopee. Link penuh atau link lain ditolak.',
        buat: 'Hubungi pelanggan — minta tukar link di Edit data, kemudian Retry.',
    },
    C2: {
        tajuk: 'Card has no video, or the run had bad input',
        apa: 'Kad yang di-Run tiada video untuk set itu, atau masa jadual tidak sah.',
        buat: 'Pelanggan kena jana video dahulu, atau betulkan slot kalendar.',
    },
    C3: {
        tajuk: 'Video file rejected by Facebook',
        apa: 'Facebook tolak fail video semasa muat naik ("Your file can\'t be uploaded") — sebab Facebook ada dalam ayat error. Biasanya fail video rosak. Tiada reel dihantar.',
        buat: 'Minta pelanggan main semula video set itu pada kad; kalau rosak, jana semula videonya, kemudian Retry.',
    },
    D1: {
        tajuk: 'Add product button missing on the page',
        apa: 'Butang Add product tak wujud pada halaman pelanggan. Mungkin page dia belum layak affiliate, mungkin robot yang tak jumpa.',
        buat: 'Tengok page pelanggan itu dahulu, baru letak dalam kumpulan yang betul.',
    },
    Z9: {
        tajuk: 'Not classified yet',
        apa: 'Error jenis baharu yang belum dikenali sistem.',
        buat: 'Baca sebab penuh, kemudian tambah kategorinya supaya ia tidak hilang dalam kumpulan ini.',
    },
};

/* Had senarai am tab "All errors". Tab Customers TIDAK terikat had ini —
   ia menyoal error pelanggan itu terus. */
const HAD_SENARAI = 500;

const katTajuk = (kod) => (KATEGORI[kod] ? KATEGORI[kod].tajuk : `Uncategorised (${kod})`);
const warnaKod = (kod) => (BALDI[String(kod || 'Z')[0]] || BALDI.Z).warna;

/* ─────────── Masa (waktu Malaysia) ─────────── */
function keDate(v) {
    if (!v) return null;
    if (typeof v.toDate === 'function') return v.toDate();
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
}
function masaPenuh(v) {
    const d = keDate(v);
    if (!d) return '—';
    return d.toLocaleString('en-MY', {
        timeZone: 'Asia/Kuala_Lumpur', day: 'numeric', month: 'short',
        hour: 'numeric', minute: '2-digit', hour12: true,
    });
}
function hariKL(v) {
    const d = keDate(v);
    return d ? new Date(d.getTime() + 8 * 3600e3).toISOString().slice(0, 10) : '';
}
function labelHari(hari) {
    if (!hari) return '—';
    const d = new Date(hari + 'T00:00:00Z');
    return d.toLocaleDateString('en-MY', { timeZone: 'UTC', day: 'numeric', month: 'short' });
}

/* ─────────── Kepingan kecil ─────────── */
function Titik({ kod }) {
    return <span className="fbh-titik" style={{ background: warnaKod(kod) }} aria-hidden="true" />;
}

function Petak({ label, nilai, nota, bahaya }) {
    return (
        <div className="fbh-petak">
            <p className="fbh-petak__label">{label}</p>
            <div className={`fbh-petak__nilai${bahaya ? ' fbh-petak__nilai--bahaya' : ''}`}>{nilai}</div>
            {nota && <div className="fbh-petak__nota">{nota}</div>}
        </div>
    );
}

/* Graf harian — bar bertindan ikut baldi. Bukan chart library: 4 bar,
   satu div satu segmen. Tinggi dinormal kepada hari paling tinggi. */
function GrafHarian({ harian }) {
    const maks = Math.max(1, ...harian.map((h) => h.A + h.B + h.C + h.D + h.Z));
    if (!harian.length) return <p className="fbh-dim">Belum ada data harian.</p>;
    return (
        <div>
            <div className="fbh-graf">
                {harian.map((h) => {
                    const jum = h.A + h.B + h.C + h.D + h.Z;
                    const tinggi = (n) => (n > 0 ? Math.max(2, Math.round((n / maks) * 108)) : 0);
                    return (
                        <div key={h.hari} className="fbh-graf__lajur" title={`${labelHari(h.hari)} — ${jum} error, ${h.pengguna} pelanggan`}>
                            <span className="fbh-graf__n">{jum}</span>
                            <div className="fbh-graf__bar">
                                {TURUTAN_BALDI.map((b) => (h[b] > 0
                                    ? <div key={b} style={{ height: tinggi(h[b]), background: BALDI[b].warna }} />
                                    : null))}
                            </div>
                            <span className="fbh-graf__hari">{labelHari(h.hari)}<br />{h.pengguna} cust</span>
                        </div>
                    );
                })}
            </div>
            <p className="fbh-dim fbh-nota-anggaran">
                Tarikh = anggaran daripada masa kad ditulis. Satu kad hanya dikira pada hari error TERAKHIRnya,
                jadi hari lepas boleh nampak lebih rendah daripada sebenar.
            </p>
        </div>
    );
}

/* ─────────── Skrin ─────────── */
export default function FbHealthTab() {
    const [ringkas, setRingkas] = useState(null);
    const [memuat, setMemuat] = useState(true);
    const [imbas, setImbas] = useState(false);
    const [ralat, setRalat] = useState('');
    const [tab, setTab] = useState('over');
    const [buka, setBuka] = useState('');

    const [errors, setErrors] = useState(null);
    const [memuatErrors, setMemuatErrors] = useState(false);
    const [cari, setCari] = useState('');
    const [penggunaBuka, setPenggunaBuka] = useState('');
    const [errPengguna, setErrPengguna] = useState({});   // uid -> error[] (dimuat bila baris dibuka)
    const [tapisBaldi, setTapisBaldi] = useState('');
    const [tapisHari, setTapisHari] = useState('all');

    const muatRingkas = useCallback(async () => {
        setMemuat(true);
        setRalat('');
        try {
            const snap = await getDoc(doc(db, 'admin_reports', 'fb_health'));
            setRingkas(snap.exists() ? snap.data() : null);
        } catch (e) {
            setRalat(e?.message || 'Gagal membaca laporan.');
        } finally {
            setMemuat(false);
        }
    }, []);

    /* Senarai error dimuat LAMBAT — hanya bila tab yang memerlukannya dibuka.
       Tab Overview tidak perlu; ia hidup daripada dokumen ringkasan sahaja. */
    const muatErrors = useCallback(async () => {
        if (errors || memuatErrors) return;
        setMemuatErrors(true);
        try {
            const q = query(
                collection(db, 'admin_reports', 'fb_health', 'errors'),
                orderBy('dilihatPada', 'desc'), limit(HAD_SENARAI),
            );
            const snap = await getDocs(q);
            setErrors(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (e) {
            setRalat(e?.message || 'Gagal membaca senarai error.');
            setErrors([]);
        } finally {
            setMemuatErrors(false);
        }
    }, [errors, memuatErrors]);

    /* Error SATU pelanggan — diambil terus, jadi ia TIDAK tertakluk pada had
       400 senarai am. Pertanyaan kesamaan uid sahaja (indeks medan tunggal
       automatik) — sengaja TIADA orderBy supaya tiada indeks komposit perlu
       dibina/deploy. Susun di sini. */
    const muatErrorPengguna = useCallback(async (uid) => {
        if (!uid || errPengguna[uid]) return;
        try {
            const snap = await getDocs(query(
                collection(db, 'admin_reports', 'fb_health', 'errors'),
                where('uid', '==', uid), limit(300),
            ));
            const senarai = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
                .sort((a, b) => (Number(b.anggaranPada?.seconds) || 0) - (Number(a.anggaranPada?.seconds) || 0));
            setErrPengguna((x) => ({ ...x, [uid]: senarai }));
        } catch {
            setErrPengguna((x) => ({ ...x, [uid]: [] }));
        }
    }, [errPengguna]);

    useEffect(() => { muatRingkas(); }, [muatRingkas]);
    useEffect(() => { if (tab === 'all') muatErrors(); }, [tab, muatErrors]);

    const jalankanImbas = async () => {
        setImbas(true);
        setRalat('');
        try {
            const fn = httpsCallable(functions, 'adminFbHealthScan');
            const r = await fn({});
            setRingkas(r?.data?.ringkas || null);
            setErrors(null);            // paksa muat semula senarai
            await muatRingkas();
        } catch (e) {
            setRalat(e?.message || 'Imbasan gagal.');
        } finally {
            setImbas(false);
        }
    };

    /* Kategori dikumpul ikut baldi — inilah susunan yang user luluskan. */
    const kumpulan = useMemo(() => {
        const ikut = (ringkas && ringkas.ikutKategori) || [];
        // D dan Z kedua-duanya "belum tahu" — digabung supaya tiada dua
        // kumpulan bertajuk sama pada skrin.
        return ['A', 'B', 'C', 'D']
            .map((b) => {
                const baris = ikut.filter((x) => {
                    const h = String(x.kunci || 'Z')[0];
                    return b === 'D' ? (h === 'D' || h === 'Z') : h === b;
                });
                return { baldi: b, baris, bil: baris.reduce((n, x) => n + x.bil, 0) };
            })
            .filter((k) => k.bil > 0);
    }, [ringkas]);

    const errorPengguna = penggunaBuka ? errPengguna[penggunaBuka] : null;

    const hariAda = useMemo(() => {
        if (!errors) return [];
        return [...new Set(errors.map((e) => hariKL(e.anggaranPada)).filter(Boolean))].sort().reverse().slice(0, 14);
    }, [errors]);

    const errorDitapis = useMemo(() => {
        if (!errors) return [];
        return errors.filter((e) => {
            if (tapisBaldi && String(e.kod || 'Z')[0] !== tapisBaldi) return false;
            if (tapisHari !== 'all' && hariKL(e.anggaranPada) !== tapisHari) return false;
            return true;
        });
    }, [errors, tapisBaldi, tapisHari]);

    const senaraiPengguna = useMemo(() => {
        const s = (ringkas && ringkas.perPengguna) || [];
        const t = cari.trim().toLowerCase();
        return t ? s.filter((p) => String(p.emel || '').toLowerCase().includes(t)) : s;
    }, [ringkas, cari]);

    if (memuat) {
        return (
            <div className="fbh-tengah">
                <Loader2 size={18} className="fbh-pusing" /> <span>Memuat laporan…</span>
            </div>
        );
    }

    const adaData = !!ringkas;
    const jumErrorA = ((ringkas && ringkas.ikutBaldi) || []).find((x) => x.kunci === 'A');

    return (
        <div className="fbh">
            {/* ── Kepala ── */}
            <div className="fbh-kepala">
                <div className="fbh-kepala__kiri">
                    <Facebook size={18} strokeWidth={1.9} />
                    <div>
                        <h2>FB Reels health</h2>
                        <p className="fbh-dim">
                            {adaData
                                ? <>Last scan: {masaPenuh(ringkas.diimbasPada)} · {ringkas.akaunDiimbas || 0} accounts scanned</>
                                : <>Belum pernah diimbas.</>}
                        </p>
                    </div>
                </div>
                <button type="button" className="fbh-btn fbh-btn--utama" onClick={jalankanImbas} disabled={imbas}>
                    {imbas ? <Loader2 size={14} className="fbh-pusing" /> : <RefreshCw size={14} />}
                    {imbas ? 'Scanning… (≈15s)' : 'Refresh scan'}
                </button>
            </div>

            {ralat && (
                <div className="fbh-jalur fbh-jalur--merah">
                    <AlertTriangle size={16} /> <span>{ralat}</span>
                </div>
            )}

            {!adaData && !ralat && (
                <div className="fbh-kosong">
                    <p>Laporan belum wujud. Tekan <strong>Refresh scan</strong> untuk imbasan pertama.</p>
                    <p className="fbh-dim">Imbasan membaca kad semua akaun — ambil masa kira-kira 15 saat.</p>
                </div>
            )}

            {adaData && (
                <>
                    {/* ── Tab ── */}
                    <div className="fbh-tabs">
                        {[
                            ['over', 'Overview'],
                            ['cust', 'Customers'],
                            ['all', 'All errors'],
                        ].map(([id, label]) => (
                            <button key={id} type="button"
                                className={`fbh-tab${tab === id ? ' fbh-tab--on' : ''}`}
                                onClick={() => setTab(id)}>{label}</button>
                        ))}
                    </div>

                    {/* ════ OVERVIEW ════ */}
                    {tab === 'over' && (
                        <>
                            {ringkas.errorSemasa > 0 ? (
                                <div className="fbh-jalur fbh-jalur--amber">
                                    <AlertTriangle size={16} />
                                    <span>
                                        Perlu perhatian. {ringkas.penggunaErrorSemasa} daripada {ringkas.penggunaFb} pelanggan
                                        ada error yang belum selesai
                                        {jumErrorA ? <>, dan {jumErrorA.bil} daripadanya amaran palsu (reel kemungkinan besar menjadi)</> : null}.
                                    </span>
                                </div>
                            ) : (
                                <div className="fbh-jalur fbh-jalur--hijau">
                                    <CheckCircle2 size={16} /> <span>Tiada error yang belum selesai.</span>
                                </div>
                            )}

                            <div className="fbh-petak-grid">
                                <Petak label="Customers affected (now)" nilai={ringkas.penggunaErrorSemasa || 0}
                                    nota={`of ${ringkas.penggunaFb || 0} using FB Reels`} />
                                <Petak label="Open errors (now)" nilai={ringkas.errorSemasa || 0} bahaya
                                    nota={jumErrorA ? `${jumErrorA.bil} are false alarms` : 'across all customers'} />
                                <Petak label="Runs logged (all time)" nilai={ringkas.cubaanLog || 0}
                                    nota={`across ${ringkas.kadFb || 0} cards`} />
                                <Petak label="Success rate (all time)" nilai={ringkas.cubaanLog ? `${Math.round((ringkas.berjayaLog / ringkas.cubaanLog) * 100)}%` : '—'}
                                    nota={`${ringkas.berjayaLog || 0} confirmed success`} />
                            </div>

                            <h3 className="fbh-h3">Errors per day</h3>
                            <GrafHarian harian={ringkas.harian || []} />

                            <div className="fbh-legend">
                                {TURUTAN_BALDI.filter((b) => b !== 'Z').map((b) => (
                                    <span key={b}><Titik kod={b} />{BALDI[b].tajuk}</span>
                                ))}
                            </div>

                            <h3 className="fbh-h3">Open errors by type</h3>
                            <div className="fbh-senarai">
                                <div className="fbh-baris fbh-baris--kepala">
                                    <span>Error type</span><span>Last seen (est.)</span>
                                    <span className="fbh-n">Errors</span><span className="fbh-n">Customers</span>
                                </div>
                                {kumpulan.map((k) => (
                                    <React.Fragment key={k.baldi}>
                                        <div className="fbh-kump">
                                            <span><Titik kod={k.baldi} />{BALDI[k.baldi].tajuk}</span>
                                            <span className="fbh-dim">{k.bil} errors</span>
                                        </div>
                                        {k.baris.map((x) => {
                                            const on = buka === x.kunci;
                                            const terkini = (ringkas.terkini || []).filter((t) => t.kod === x.kunci);
                                            return (
                                                <React.Fragment key={x.kunci}>
                                                    <button type="button"
                                                        className={`fbh-baris fbh-baris--klik${on ? ' fbh-baris--on' : ''}`}
                                                        onClick={() => setBuka(on ? '' : x.kunci)}>
                                                        <span className="fbh-baris__tajuk">
                                                            <ChevronRight size={14} className={`fbh-chev${on ? ' fbh-chev--on' : ''}`} />
                                                            {katTajuk(x.kunci)}
                                                        </span>
                                                        <span className="fbh-dim">{masaPenuh(x.kaliAkhir)}</span>
                                                        <span className="fbh-n">{x.bil}</span>
                                                        <span className="fbh-n">{x.pengguna}</span>
                                                    </button>
                                                    {on && (
                                                        <div className="fbh-detail">
                                                            <p className="fbh-label">Apa maksudnya</p>
                                                            <p>{KATEGORI[x.kunci] ? KATEGORI[x.kunci].apa : 'Error jenis baharu yang belum ada penjelasan.'}</p>
                                                            <p className="fbh-label">Apa perlu dibuat</p>
                                                            <p>{KATEGORI[x.kunci] ? KATEGORI[x.kunci].buat : 'Baca sebab penuh pada tab All errors.'}</p>
                                                            {terkini.length > 0 && (
                                                                <>
                                                                    <p className="fbh-label">Latest</p>
                                                                    {terkini.slice(0, 3).map((t, i) => (
                                                                        <div key={i} className="fbh-ev">
                                                                            <span>{masaPenuh(t.pada)}</span>
                                                                            <span>{t.emel || '—'}</span>
                                                                            <span>{t.produk || '—'} · {t.set}</span>
                                                                        </div>
                                                                    ))}
                                                                </>
                                                            )}
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}
                                {kumpulan.length === 0 && <div className="fbh-baris"><span className="fbh-dim">Tiada error yang belum selesai.</span></div>}
                            </div>

                            {(ringkas.takDikenali || []).length > 0 && (
                                <p className="fbh-dim fbh-nota-anggaran">
                                    Langkah belum dikelaskan: {ringkas.takDikenali.map((u) => `${u.kunci} (${u.bil})`).join(' · ')}
                                </p>
                            )}

                            <p className="fbh-dim fbh-nota-anggaran">
                                ⚠️ Laporan ini hanya nampak kegagalan yang sempat ditulis pada kad pelanggan.
                                Larian yang mati sebelum bermula — contoh toggle &ldquo;Allow user scripts&rdquo; mati atau
                                skrip gagal dimuat — tidak direkod di mana-mana, jadi ia TIDAK termasuk di sini.
                            </p>
                        </>
                    )}

                    {/* ════ CUSTOMERS ════ */}
                    {tab === 'cust' && (
                        <>
                            <div className="fbh-cari">
                                <Search size={15} />
                                <input value={cari} onChange={(e) => setCari(e.target.value)}
                                    placeholder="Search customer email" />
                            </div>
                            <div className="fbh-senarai">
                                <div className="fbh-baris fbh-baris--kepala fbh-baris--cust">
                                    <span>Customer</span><span className="fbh-n">Tried</span><span className="fbh-n">Failed</span>
                                    <span className="fbh-n">Open</span><span>Main problem</span><span>Last (est.)</span>
                                </div>
                                {senaraiPengguna.map((p) => {
                                    const on = penggunaBuka === p.uid;
                                    return (
                                        <React.Fragment key={p.uid}>
                                            <button type="button"
                                                className={`fbh-baris fbh-baris--cust fbh-baris--klik${on ? ' fbh-baris--on' : ''}`}
                                                onClick={() => { const b = on ? '' : p.uid; setPenggunaBuka(b); if (b) muatErrorPengguna(b); }}>
                                                <span className="fbh-baris__tajuk">{p.emel || p.uid}</span>
                                                <span className="fbh-n">{p.cuba}</span>
                                                <span className="fbh-n">{p.gagal}</span>
                                                <span className="fbh-n">{p.masihGagal}</span>
                                                <span className="fbh-dim">{p.utama ? <><Titik kod={p.utama} />{katTajuk(p.utama)}</> : '—'}</span>
                                                <span className="fbh-dim">{p.akhir ? masaPenuh(p.akhir) : '—'}</span>
                                            </button>
                                            {on && (
                                                <div className="fbh-detail">
                                                    {p.whatsapp && (
                                                        <p><span className="fbh-label">WhatsApp</span>{' '}
                                                            <a href={`https://wa.me/${String(p.whatsapp).replace(/\D/g, '')}`}
                                                                target="_blank" rel="noreferrer">{p.whatsapp}</a></p>
                                                    )}
                                                    <p className="fbh-label">Open errors</p>
                                                    {!errorPengguna && <p className="fbh-dim">Memuat…</p>}
                                                    {errorPengguna && errorPengguna.length === 0 && <p className="fbh-dim">Tiada rekod error terbuka.</p>}
                                                    {(errorPengguna || []).slice(0, 60).map((e) => (
                                                        <div key={e.id} className="fbh-ev">
                                                            <span>{masaPenuh(e.anggaranPada)}{e.jam ? ` (log ${e.jam})` : ''}</span>
                                                            <span><Titik kod={e.kod} />{katTajuk(e.kod)}</span>
                                                            <span>{e.produk || '—'} · {e.set}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                                {senaraiPengguna.length === 0 && <div className="fbh-baris"><span className="fbh-dim">Tiada pelanggan sepadan.</span></div>}
                            </div>
                        </>
                    )}

                    {/* ════ ALL ERRORS ════ */}
                    {tab === 'all' && (
                        <>
                            <div className="fbh-tapis">
                                <div className="fbh-tapis__set">
                                    {[['', 'All'], ...TURUTAN_BALDI.filter((b) => b !== 'Z').map((b) => [b, BALDI[b].tajuk.split(' —')[0]])].map(([v, l]) => (
                                        <button key={v || 'all'} type="button"
                                            className={`fbh-pil${tapisBaldi === v ? ' fbh-pil--on' : ''}`}
                                            onClick={() => setTapisBaldi(v)}>{l}</button>
                                    ))}
                                </div>
                                <select value={tapisHari} onChange={(e) => setTapisHari(e.target.value)} className="fbh-select">
                                    <option value="all">All dates</option>
                                    {hariAda.map((h) => <option key={h} value={h}>{labelHari(h)}</option>)}
                                </select>
                            </div>
                            {memuatErrors && <div className="fbh-tengah"><Loader2 size={16} className="fbh-pusing" /> <span>Memuat…</span></div>}
                            {!memuatErrors && (
                                <div className="fbh-senarai">
                                    <div className="fbh-baris fbh-baris--kepala fbh-baris--all">
                                        <span>Date and time (est.)</span><span>Customer</span>
                                        <span>Error type</span><span>Product · set</span>
                                    </div>
                                    {errorDitapis.slice(0, 200).map((e) => (
                                        <div key={e.id} className="fbh-baris fbh-baris--all">
                                            <span className="fbh-mono">{masaPenuh(e.anggaranPada)}</span>
                                            <span className="fbh-dim">{e.emel || e.uid}</span>
                                            <span title={e.ayat || e.step}><Titik kod={e.kod} />{katTajuk(e.kod)}</span>
                                            <span className="fbh-dim">{e.produk || '—'} · {e.set}</span>
                                        </div>
                                    ))}
                                    {errorDitapis.length === 0 && <div className="fbh-baris"><span className="fbh-dim">Tiada error sepadan.</span></div>}
                                </div>
                            )}
                            {errorDitapis.length > 200 && (
                                <p className="fbh-dim fbh-nota-anggaran">Memapar 200 daripada {errorDitapis.length}.</p>
                            )}
                            {errors && errors.length >= HAD_SENARAI && (
                                <p className="fbh-dim fbh-nota-anggaran">
                                    ⚠️ Senarai ini dihadkan kepada {HAD_SENARAI} error terkini. Untuk melihat
                                    SEMUA error seorang pelanggan, buka dia dalam tab Customers — di sana
                                    error diambil terus tanpa had ini.
                                </p>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}
